// An issue's place in its column (poulpe-model, sort_key.rs): a key of
// today's `fractional-indexing` 4.0.0, in its default alphabets, compared as
// plain strings, a column showing its issues from the smallest. The server
// checks the key a move names, and makes a new issue's at the top of its
// column, by these same rules: so the store shows at once what the server
// will answer, and the mock answers as the server does.
import { generateKeyBetween } from 'fractional-indexing'

/** A valid key between the neighbours; null when no supported key fits. */
export function keyBetween(lower: string | null, upper: string | null): string | null {
  try {
    const key = generateKeyBetween(lower, upper)
    return isSortKey(key) ? key : null
  } catch {
    return null
  }
}

/** The longest a key may be, in characters. */
export const SORT_KEY_MAX = 128

/** The digits of a key, in their order: base 62. */
const DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'

/** The smallest integer part, `A` and 26 zeros, which is no key alone. */
const SMALLEST_INTEGER = 'A' + '0'.repeat(26)

/** How long an integer part with this head is: `a` 2 to `z` 27, `Z` 2 to `A` 27; null for a character that is no head. */
function integerLength(head: string): number | null {
  if (head >= 'a' && head <= 'z' && head.length === 1) return head.charCodeAt(0) - 'a'.charCodeAt(0) + 2
  if (head >= 'A' && head <= 'Z' && head.length === 1) return 'Z'.charCodeAt(0) - head.charCodeAt(0) + 2
  return null
}

/**
 * Whether `key` is a sort key: an integer part as long as its head says,
 * then a fraction that does not end with `0`, all of base-62 digits; not the
 * smallest integer alone; and at most SORT_KEY_MAX characters. A character
 * outside the digits is refused, where the package would read it as a zero.
 */
export function isSortKey(key: string): boolean {
  if (key.length === 0 || key.length > SORT_KEY_MAX || !/^[0-9A-Za-z]+$/.test(key)) return false
  const length = integerLength(key[0]!)
  if (length === null || key.length < length || key === SMALLEST_INTEGER) return false
  return !(key.length > length && key.endsWith('0'))
}

/** The integer part just below `integer`, as the package's `decrementInteger` makes it; null below the smallest. */
function decrement(integer: string): string | null {
  const largest = DIGITS[DIGITS.length - 1]!
  // Digits that borrow become the largest digit, until one can lend.
  let borrowed = 0
  for (let place = integer.length - 1; place >= 1; place--) {
    const index = DIGITS.indexOf(integer[place]!)
    if (index < 0) return null
    if (index > 0) return integer.slice(0, place) + DIGITS[index - 1] + largest.repeat(borrowed)
    borrowed += 1
  }
  // Every digit borrowed: the head moves one down, and the digits fit its length.
  const head = integer[0]!
  if (head === 'A') return null
  const lower = head === 'a' ? 'Z' : String.fromCharCode(head.charCodeAt(0) - 1)
  const length = integerLength(lower)
  return length === null ? null : lower + largest.repeat(length - 1)
}

/**
 * The key just before `key`, as the package's `generateKeyBetween(null, key)`
 * makes it: the key's integer part when it has a fraction, otherwise the
 * integer just below. At the smallest integer, makes a smaller fraction;
 * if decrementing makes that integer alone, gives it the fraction V.
 * Null for an invalid key or when the result would exceed SORT_KEY_MAX.
 */
export function keyBefore(key: string): string | null {
  if (!isSortKey(key)) return null
  const integer = key.slice(0, integerLength(key[0]!)!)
  let made: string | null
  if (integer === SMALLEST_INTEGER) {
    const fraction = key.slice(integer.length)
    const first = fraction.search(/[1-9A-Za-z]/)
    const digit = DIGITS.indexOf(fraction[first]!)
    const lower = digit === 1 && first + 1 === fraction.length ? '0V' : DIGITS[Math.ceil(digit / 2)]!
    made = integer + fraction.slice(0, first) + lower
  } else {
    made = integer.length < key.length ? integer : decrement(integer)
    if (made === SMALLEST_INTEGER) made += 'V'
  }
  return made !== null && isSortKey(made) ? made : null
}

/**
 * The key of an issue put at the top of a column whose first key is
 * `first`: just before it, or `a0` in an empty column. When a key before
 * it would exceed the length limit, it shares the first's own.
 */
export function topKey(first: string | null): string {
  if (first === null) return 'a0'
  return keyBefore(first) ?? first
}

/** The smallest of `keys`, as a column shows them; null for none. */
export function firstKey(keys: Iterable<string>): string | null {
  let first: string | null = null
  for (const key of keys) if (first === null || key < first) first = key
  return first
}
